import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Utility: escape HTML to avoid injecting raw HTML into temporary container
function escapeHtml(str) {
  if (str === undefined || str === null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Utility: Format date to DD-MM-YYYY or fallback
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const s = String(dateStr).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s.replace(/\//g, "-");
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }
  const date = new Date(s);
  if (isNaN(date)) return dateStr;
  // Convert to dd-mm-yyyy
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

async function generatePDF(voucher = {}) {
  // Create a temporary DOM node for rendering the voucher
  const container = document.createElement("div");
  container.style.width = "700px";
  container.style.padding = "24px";
  container.style.background = "#fff";
  container.style.fontFamily = "Arial, sans-serif";
  container.style.boxSizing = "border-box";
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";

  // Build content using escaped values
  const voucherNo = escapeHtml(voucher.voucher_no || voucher.id || "-");
  const date = escapeHtml(formatDate(voucher.date));
  const account = escapeHtml(voucher.account_head || voucher.account || "-");
  const reference = escapeHtml(voucher.reference || "-");
  const mode = escapeHtml(voucher.mode || "-");
  const amount = Number(voucher.amount || 0);
  const amountStr = `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const paidTo = escapeHtml(voucher.paid_to || voucher.payee || "-");
  const status = escapeHtml(voucher.status || "Processed");
  const particulars = escapeHtml(voucher.particulars || "-");
  const remarks = escapeHtml(voucher.remarks || "-");

  container.innerHTML = `
    <div style="width:100%; box-sizing:border-box; color:#222;">
      <h2 style="text-align:center;margin-bottom:18px;font-size:18px;margin-top:0;">Expense Voucher</h2>
      <table style="width:100%;margin-bottom:12px;font-size:13px;border-collapse:collapse;">
        <tbody>
          <tr>
            <td style="padding:6px 0;"><strong>Voucher No:</strong> ${voucherNo}</td>
            <td style="padding:6px 0;text-align:right;"><strong>Date:</strong> ${date}</td>
          </tr>
          <tr>
            <td style="padding:6px 0;"><strong>Account:</strong> ${account}</td>
            <td style="padding:6px 0;text-align:right;"><strong>Reference:</strong> ${reference}</td>
          </tr>
          <tr>
            <td style="padding:6px 0;"><strong>Mode:</strong> ${mode}</td>
            <td style="padding:6px 0;text-align:right;"><strong>Amount:</strong> ${amountStr}</td>
          </tr>
          <tr>
            <td style="padding:6px 0;"><strong>Paid To:</strong> ${paidTo}</td>
            <td style="padding:6px 0;text-align:right;"><strong>Status:</strong> ${status}</td>
          </tr>
        </tbody>
      </table>

      <div style="margin:12px 0;font-size:13px;">
        <strong>Particulars:</strong>
        <div style="margin-top:6px;padding:8px;border:1px solid #eaeaea;border-radius:4px;background:#fafafa;">${particulars}</div>
      </div>

      <div style="margin:12px 0;font-size:13px;">
        <strong>Remarks:</strong>
        <div style="margin-top:6px;padding:8px;border:1px solid #eaeaea;border-radius:4px;background:#fafafa;">${remarks}</div>
      </div>

      <div style="margin-top:28px;text-align:right;font-size:11px;color:#555;">
        <i>This is a system-generated voucher.</i>
      </div>
    </div>
  `;

  // Append, render to canvas and generate PDF
  document.body.appendChild(container);
  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pdfWidth = pageWidth * 0.9;
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    const marginLeft = (pageWidth - pdfWidth) / 2;
    pdf.addImage(imgData, "PNG", marginLeft, 40, pdfWidth, pdfHeight);
    const safeFileName = `Voucher_${(voucher.voucher_no || voucher.id || "Expense").toString().replace(/[^a-zA-Z0-9-_]/g, "_")}.pdf`;
    pdf.save(safeFileName);
  } finally {
    // Clean up DOM even if html2canvas/pdf fails
    if (container && container.parentNode) document.body.removeChild(container);
  }
}

const voucherUtils = {
  generatePDF,
};

export default voucherUtils;
